/**
 * SSGI sized at the render scale (Phase 8, the Phase 6 deferral). three r186's `SSGINode`
 * resizes its targets from `getDrawingBufferSize` every frame, so dynamic resolution shrank
 * the scene pass but never the GI. This subclass multiplies the requested size by the current
 * scale; the post graph updates the scale on every DRS step through `setScale`.
 */
import type { Node, PerspectiveCamera } from 'three/webgpu';
import SSGINode from 'three/addons/tsl/display/SSGINode.js';

/** The base class sizes itself through a method the typings leave undeclared. */
interface Sizable {
  setSize(width: number, height: number): void;
}

/** The GI targets follow the render scale in tenths, with half a step of hysteresis, so a
 * dynamic-resolution controller hunting around a value does not reallocate them every step. */
const STEP = 0.1;

export class ScaledSsgiNode extends SSGINode {
  /** 0.5..1: the render scale the GI targets follow. */
  scale = 1;
  private applied = 1;

  constructor(beauty: Node, depth: Node, normal: Node, camera: PerspectiveCamera) {
    super(beauty, depth, normal, camera);
  }

  setSize(width: number, height: number): void {
    if (Math.abs(this.scale - this.applied) > STEP * 0.75) {
      this.applied = Math.round(this.scale / STEP) * STEP;
    }
    const base = SSGINode.prototype as unknown as Sizable;
    base.setSize.call(
      this,
      Math.max(1, Math.round(width * this.applied)),
      Math.max(1, Math.round(height * this.applied)),
    );
  }
}
